import React, { useState, useEffect } from 'react';

const AchievementUnlockAnimation = ({ achievement, onComplete }) => {
  const [stage, setStage] = useState('entering'); // entering, celebrating, exiting
  const [particles, setParticles] = useState([]);
  const [congratsMessage, setCongratsMessage] = useState('');

  useEffect(() => {
    if (!achievement) return;

    // Generate celebration particles with more variety
    const newParticles = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 0.8,
      duration: 1.5 + Math.random() * 2.5,
      color: ['#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#10b981', '#f97316', '#ec4899'][Math.floor(Math.random() * 7)]
    }));
    setParticles(newParticles);

    // Set personalized congratulatory message
    setCongratsMessage(getCongratulationsMessage(achievement));

    // Extended animation sequence for major achievements
    const timer1 = setTimeout(() => setStage('celebrating'), 600);
    const timer2 = setTimeout(() => setStage('exiting'), 4000);
    const timer3 = setTimeout(() => {
      setStage('complete');
      onComplete?.();
    }, 4800);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [achievement, onComplete]);

  // Generate personalized congratulations message
  const getCongratulationsMessage = (achievement) => {
    if (!achievement || !achievement.definition) return "Incredible achievement!";
    
    const { name, category } = achievement.definition;
    
    const congratulationsByType = {
      'Campaign Creator': "Welcome to the creator's journey! Your first campaign is just the beginning of amazing things to come! 🚀",
      'Campaign Veteran': "Five campaigns down! You're building incredible momentum and showing real dedication! 💪",
      'Campaign Master': "Ten campaigns! You've officially mastered the art of content creation. Your skills are truly impressive! 🏆",
      'Social Butterfly': "Your sharing spirit is inspiring! Ten shares means you're helping spread amazing content to the world! 🌍",
      'Creative Genius': "One hundred pieces of creative content! Your imagination knows no bounds! 🎨",
      'Campaign Legend': "One hundred campaigns! You're a true legend in the marketing world. This is extraordinary! 👑",
      'Veteran Marketer': "100 days of consistent creativity! Your dedication and persistence are truly remarkable! 🌟"
    };
    
    // Return specific message or generate category-based message
    if (congratulationsByType[name]) {
      return congratulationsByType[name];
    }
    
    const categoryMessages = {
      milestone: "You've reached an incredible milestone! Your progress is inspiring and shows real dedication! 🎯",
      creative: "Your creative energy is absolutely amazing! Keep pushing the boundaries of what's possible! ✨",
      engagement: "Your consistent engagement is paying off! This level of dedication is truly admirable! 📈",
      social: "Your social presence is making a real impact! Keep connecting and sharing your awesome work! 🤝",
      streak: "What an incredible streak! Your consistency and determination are absolutely phenomenal! 🔥"
    };
    
    return categoryMessages[category] || "Outstanding achievement! You're doing incredible work! 🌟";
  };

  const getCategoryGradient = (category) => {
    const gradients = {
      milestone: 'from-purple-400 via-purple-500 to-purple-600',
      creative: 'from-blue-400 via-blue-500 to-blue-600',
      engagement: 'from-green-400 via-green-500 to-green-600',
      social: 'from-orange-400 via-orange-500 to-orange-600',
      streak: 'from-red-400 via-red-500 to-red-600',
    };
    return gradients[category] || 'from-gray-400 via-gray-500 to-gray-600';
  };

  const getCategoryEmoji = (category) => {
    const emojis = {
      milestone: '🎯',
      creative: '🎨',
      engagement: '📈',
      social: '🌍',
      streak: '🔥'
    };
    return emojis[category] || '🏆';
  };

  if (!achievement || stage === 'complete') return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
      {/* Enhanced Celebration Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={`absolute w-3 h-3 rounded-full opacity-0 ${
              stage === 'celebrating' ? 'animate-bounce' : ''
            }`}
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              backgroundColor: particle.color,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              animationFillMode: 'forwards',
              opacity: stage === 'celebrating' ? 1 : 0,
              transition: 'opacity 0.6s ease-in-out',
              boxShadow: '0 0 6px rgba(255, 255, 255, 0.5)'
            }}
          />
        ))}
      </div>

      {/* Enhanced Confetti Effect */}
      {stage === 'celebrating' && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {Array.from({ length: 80 }, (_, i) => (
            <div
              key={`confetti-${i}`}
              className="absolute w-2 h-4 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-10px`,
                backgroundColor: ['#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#10b981', '#f97316', '#ec4899'][i % 7],
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: '4s',
                transform: `rotate(${Math.random() * 360}deg)`,
                animation: `confettiFall 4s linear ${Math.random() * 2}s forwards`
              }}
            />
          ))}
        </div>
      )}

      {/* Main Achievement Card */}
      <div className={`relative transform transition-all duration-800 ease-out ${
        stage === 'entering' ? 'scale-0 rotate-180 opacity-0' :
        stage === 'celebrating' ? 'scale-105 rotate-0 opacity-100' :
        'scale-90 opacity-0'
      }`}>
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 max-w-lg mx-4 border-4 border-yellow-400 relative overflow-hidden">
          {/* Background glow effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-100 via-orange-50 to-purple-50 dark:from-yellow-900/20 dark:via-orange-900/20 dark:to-purple-900/20 rounded-3xl"></div>
          
          {/* Header */}
          <div className="relative text-center mb-6">
            <div className="inline-flex items-center space-x-3 mb-3">
              <div className="text-4xl animate-bounce">🎉</div>
              <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 via-orange-500 to-purple-500">
                Outstanding!
              </h2>
              <div className="text-4xl animate-bounce" style={{ animationDelay: '0.3s' }}>✨</div>
            </div>
            <div className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-orange-600 mb-2">
              Achievement Unlocked
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {getCategoryEmoji(achievement.definition?.category)} You've reached a new milestone! {getCategoryEmoji(achievement.definition?.category)}
            </div>
          </div>

          {/* Achievement Badge */}
          <div className="relative flex justify-center mb-6">
            <div className={`relative w-28 h-28 rounded-full flex items-center justify-center text-5xl bg-gradient-to-br ${getCategoryGradient(achievement.definition?.category)} text-white shadow-2xl ${
              stage === 'celebrating' ? 'animate-pulse' : ''
            }`}>
              {achievement.definition?.badge_icon || '🏆'}
              
              {/* Enhanced glow effects */}
              <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${getCategoryGradient(achievement.definition?.category)} opacity-40 ${
                stage === 'celebrating' ? 'animate-ping' : ''
              }`}></div>
              
              <div className={`absolute -inset-1 rounded-full bg-gradient-to-br ${getCategoryGradient(achievement.definition?.category)} opacity-20 ${
                stage === 'celebrating' ? 'animate-ping' : ''
              }`} style={{ animationDelay: '0.5s' }}></div>
              
              {/* Ring effects */}
              <div className={`absolute -inset-3 rounded-full border-3 border-yellow-400 ${
                stage === 'celebrating' ? 'animate-spin' : ''
              }`}></div>
              
              <div className={`absolute -inset-4 rounded-full border-2 border-orange-300 opacity-50 ${
                stage === 'celebrating' ? 'animate-spin' : ''
              }`} style={{ animationDelay: '0.3s', animationDuration: '3s' }}></div>
            </div>
          </div>

          {/* Achievement Details */}
          <div className="relative text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              {achievement.definition?.name}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4 text-base leading-relaxed">
              {achievement.definition?.description}
            </p>
            
            {/* Personalized congratulations message */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-xl p-4 mb-4 border-2 border-yellow-200 dark:border-yellow-700">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 italic">
                {congratsMessage}
              </p>
            </div>
            
            {/* Category and Points */}
            <div className="flex items-center justify-center space-x-6">
              <div className={`px-4 py-2 rounded-full text-base font-bold border-3 transform hover:scale-105 transition-transform ${
                achievement.definition?.category === 'milestone' ? 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/40 dark:text-purple-300' :
                achievement.definition?.category === 'creative' ? 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/40 dark:text-blue-300' :
                achievement.definition?.category === 'engagement' ? 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-300' :
                achievement.definition?.category === 'social' ? 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/40 dark:text-orange-300' :
                achievement.definition?.category === 'streak' ? 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-300' :
                'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-900/40 dark:text-gray-300'
              }`}>
                {achievement.definition?.category}
              </div>
              <div className="text-3xl font-bold bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent flex items-center">
                +{achievement.definition?.points}
                <span className="ml-2 text-lg">points</span>
              </div>
            </div>
          </div>

          {/* Enhanced Progress Indicator */}
          <div className="relative flex justify-center space-x-3 mb-6">
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`w-3 h-3 rounded-full transition-all duration-500 ${
                  stage === 'entering' && step === 1 ? 'bg-yellow-500 scale-125' :
                  stage === 'celebrating' && step <= 2 ? 'bg-yellow-500 scale-125' :
                  stage === 'exiting' && step <= 4 ? 'bg-yellow-500 scale-125' :
                  'bg-gray-300 dark:bg-gray-600'
                }`}
              />
            ))}
          </div>

          {/* Action Button */}
          <div className="relative text-center">
            <button
              onClick={() => {
                setStage('exiting');
                setTimeout(() => onComplete?.(), 400);
              }}
              className="px-8 py-3 bg-gradient-to-r from-yellow-500 via-orange-500 to-purple-500 text-white rounded-xl font-bold text-lg hover:from-yellow-600 hover:via-orange-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-xl"
            >
              Amazing! Let's Continue! 🚀
            </button>
          </div>
        </div>

        {/* Enhanced Sparkle Effects */}
        {stage === 'celebrating' && (
          <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 12 }, (_, i) => (
              <div
                key={`sparkle-${i}`}
                className="absolute text-yellow-400 text-2xl animate-ping"
                style={{
                  left: `${[5, 95, 15, 85, 25, 75, 35, 65, 45, 55, 10, 90][i]}%`,
                  top: `${[5, 15, 85, 95, 25, 75, 45, 55, 65, 35, 20, 80][i]}%`,
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: '2.5s'
                }}
              >
                {['✨', '🌟', '💫', '⭐'][i % 4]}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Enhanced CSS for confetti animation */}
      <style jsx>{`
        @keyframes confettiFall {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default AchievementUnlockAnimation;